import React, { useState } from "react";
import { Pressable, StyleSheet, TextInput, View } from "react-native";
import {
  ArrowsLeftRightIcon,
  CheckIcon,
  DotsThreeVerticalIcon,
  PencilSimpleIcon,
  TrashIcon,
  XIcon,
} from "@phosphor-icons/react";

import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomText from "../../../common/general/CustomText/CustomText";
import type { ActiveRubricSetQuery } from "@axio/graphql/generated/graphql";

type RubricSetViewModel = NonNullable<ActiveRubricSetQuery["activeRubricSet"]["data"]>;
type RubricSectionViewModel = RubricSetViewModel["sections"][number];
type RubricCriterionViewModel = RubricSectionViewModel["criteria"][number];

interface RubricCriterionCardProps {
  criterion: RubricCriterionViewModel;
  sectionId: string;
  sections: RubricSectionViewModel[];
  isSaving: boolean;
  onSave: (criterionId: string, title: string, description: string) => void;
  onMove: (criterionId: string, sectionId: string) => void;
  onDelete: (criterionId: string) => void;
}

function RubricCriterionCard({
  criterion,
  sectionId,
  sections,
  isSaving,
  onSave,
  onMove,
  onDelete,
}: RubricCriterionCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const [title, setTitle] = useState(criterion.title);
  const [description, setDescription] = useState(criterion.description);
  const otherSections = sections.filter((section) => section.id !== sectionId);
  const isModified = criterion.isEdited || criterion.isDeleted;

  const handleCancel = () => {
    setTitle(criterion.title);
    setDescription(criterion.description);
    setIsEditing(false);
  };

  const handleSave = () => {
    onSave(criterion.id, title, description);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <View style={styles.editCard}>
        <View style={styles.criterionTitleRow}>
          {criterion.code ? (
            <CustomText
              text={criterion.code}
              size="xs"
              weight="bold"
              style={styles.criterionCode}
              useTranslationText={false}
            />
          ) : null}
          <TextInput
            accessibilityLabel="Criterion title"
            value={title}
            onChangeText={setTitle}
            style={styles.titleInput}
          />
        </View>
        <TextInput
          accessibilityLabel="Criterion description"
          value={description}
          onChangeText={setDescription}
          multiline
          style={styles.descriptionInput}
        />
        <View style={styles.editActions}>
          <CustomButton
            text="Save"
            size="s"
            styleType="primary"
            disabled={isSaving}
            useTranslationText={false}
            onPress={handleSave}
            leftIcon={<CheckIcon color="#FFFFFF" size={14} weight="bold" />}
          />
          <CustomButton
            text="Cancel"
            size="s"
            styleType="light"
            disabled={isSaving}
            useTranslationText={false}
            onPress={handleCancel}
          />
        </View>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.criterionCard,
        isModified ? styles.modifiedCriterionCard : null,
        menuOpen ? styles.criterionCardMenuOpen : null,
      ]}>
      <View style={styles.criterionContentRow}>
        <View style={styles.criterionText}>
          <View style={styles.criterionTitleRow}>
            {criterion.code ? (
              <CustomText
                text={criterion.code}
                size="xs"
                weight="bold"
                style={styles.criterionCode}
                useTranslationText={false}
              />
            ) : null}
            <CustomText
              text={criterion.title}
              size="xs"
              weight="medium"
              style={styles.criterionTitle}
              useTranslationText={false}
            />
            {isModified ? (
              <View style={styles.editedBadge}>
                <CustomText
                  text="Edited"
                  size="xs"
                  weight="bold"
                  style={styles.editedBadgeText}
                  useTranslationText={false}
                />
              </View>
            ) : null}
          </View>
          <CustomText
            text={criterion.description}
            size="xs"
            style={styles.criterionDescription}
            useTranslationText={false}
          />
        </View>

        <View style={styles.menuContainer}>
          <Pressable
            role="button"
            accessibilityLabel="Criterion actions"
            onPress={() => {
              setMenuOpen((value) => !value);
              setMoveMenuOpen(false);
            }}
            style={styles.actionButton}>
            {menuOpen ? (
              <XIcon color="#667085" size={14} weight="bold" />
            ) : (
              <DotsThreeVerticalIcon color="#667085" size={18} weight="bold" />
            )}
          </Pressable>
          {menuOpen ? (
            <View style={styles.menu}>
              <Pressable
                role="button"
                accessibilityLabel="Edit criterion"
                onPress={() => {
                  setMenuOpen(false);
                  setIsEditing(true);
                }}
                style={styles.menuItem}>
                <PencilSimpleIcon color="#344054" size={14} weight="bold" />
                <CustomText text="Edit" size="xs" style={styles.menuText} useTranslationText={false} />
              </Pressable>
              <Pressable
                role="button"
                accessibilityLabel="Move criterion to another category"
                onPress={() => setMoveMenuOpen((value) => !value)}
                style={styles.menuItem}>
                <ArrowsLeftRightIcon color="#344054" size={14} weight="bold" />
                <CustomText text="Move to..." size="xs" style={styles.menuText} useTranslationText={false} />
              </Pressable>
              {moveMenuOpen ? (
                <View style={styles.subMenu}>
                  {otherSections.map((section) => (
                    <Pressable
                      key={section.id}
                      role="button"
                      accessibilityLabel={`Move criterion to ${section.title}`}
                      onPress={() => {
                        setMenuOpen(false);
                        setMoveMenuOpen(false);
                        onMove(criterion.id, section.id);
                      }}
                      style={styles.subMenuItem}>
                      <CustomText text={section.title} size="xs" style={styles.menuText} useTranslationText={false} />
                    </Pressable>
                  ))}
                </View>
              ) : null}
              <View style={styles.menuSeparator} />
              <Pressable
                role="button"
                accessibilityLabel="Delete criterion"
                onPress={() => {
                  setMenuOpen(false);
                  onDelete(criterion.id);
                }}
                style={styles.menuItem}>
                <TrashIcon color="#B42318" size={14} weight="bold" />
                <CustomText text="Delete" size="xs" style={styles.deleteText} useTranslationText={false} />
              </Pressable>
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  criterionCard: {
    position: "relative",
    zIndex: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    gap: 4,
    overflow: "visible",
  },
  criterionCardMenuOpen: {
    zIndex: 100,
  },
  modifiedCriterionCard: {
    backgroundColor: "#FFFAEB",
    borderWidth: 1,
    borderColor: "#FEDF89",
  },
  editCard: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#84ADFF",
    gap: 8,
  },
  criterionContentRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  criterionText: {
    flex: 1,
    minWidth: 0,
  },
  criterionTitleRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  criterionCode: {
    color: "#3459D4",
  },
  criterionTitle: {
    color: "#101828",
    flex: 1,
  },
  criterionDescription: {
    color: "#475467",
    lineHeight: 18,
  },
  editedBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: "#FEDF89",
  },
  editedBadgeText: {
    color: "#93370D",
  },
  actionButton: {
    alignItems: "center",
    justifyContent: "center",
    minWidth: 32,
    minHeight: 32,
    borderRadius: 8,
  },
  menuContainer: {
    position: "relative",
    zIndex: 101,
  },
  menu: {
    position: "absolute",
    right: 0,
    top: 36,
    zIndex: 102,
    elevation: 8,
    width: 180,
    padding: 4,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#EAECF0",
    shadowColor: "#101828",
    shadowOpacity: 0.12,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 8 },
  },
  menuItem: {
    minHeight: 34,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  subMenu: {
    paddingLeft: 22,
    paddingBottom: 4,
    gap: 2,
  },
  subMenuItem: {
    minHeight: 30,
    justifyContent: "center",
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  menuSeparator: {
    height: 1,
    backgroundColor: "#EAECF0",
    marginVertical: 4,
  },
  menuText: {
    color: "#344054",
  },
  deleteText: {
    color: "#B42318",
  },
  titleInput: {
    flex: 1,
    minHeight: 32,
    borderBottomWidth: 1,
    borderBottomColor: "#D0D5DD",
    color: "#101828",
    fontSize: 12,
    fontWeight: "500",
  },
  descriptionInput: {
    minHeight: 72,
    padding: 8,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    borderRadius: 8,
    color: "#344054",
    fontSize: 12,
    lineHeight: 18,
    textAlignVertical: "top",
  },
  editActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
});

export default RubricCriterionCard;
